import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { money, num } from '../lib/format.ts';

interface SkuDetail {
  id: number;
  productId: string;
  name: string;
  description: string | null;
  brand: string | null;
  category: string | null;
  location: string | null;
  unit: string | null;
  stockQty: number;
  price: number;
  ptype: string;
  isDataQualityFlag: boolean;
}

export function SkuDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['sku', id],
    queryFn: () =>
      apiFetch<SkuDetail>(`/api/dashboard/skus/${encodeURIComponent(id ?? '')}`),
    enabled: !!id,
  });

  return (
    <>
      <BackToSkus />

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <>
          <header className="main-header">
            <div>
              <h1 className="main-title">{data.name || data.productId}</h1>
              <span className="main-subtitle mono">{data.productId}</span>
            </div>
            {data.isDataQualityFlag && (
              <span className="badge badge-pending">flagged · catch-all</span>
            )}
          </header>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Product</h2>
            </header>
            <dl className="detail-grid">
              <dt>SKU</dt>
              <dd className="mono">{data.productId}</dd>
              <dt>Brand</dt>
              <dd>{data.brand || '—'}</dd>
              <dt>Category</dt>
              <dd>{data.category || '—'}</dd>
              <dt>Description</dt>
              <dd>{data.description || '—'}</dd>
              <dt>Location</dt>
              <dd className="mono">{data.location || '—'}</dd>
              <dt>Unit</dt>
              <dd>{data.unit || '—'}</dd>
              <dt>Stock qty</dt>
              <dd>{num(data.stockQty)}</dd>
              <dt>Price</dt>
              <dd>{money(data.price)}</dd>
              <dt>Type</dt>
              <dd>{data.ptype === 'I' ? 'Stock item' : 'Service'}</dd>
            </dl>
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Sales history</h2>
              <span className="panel-subtitle">requires line-items sync</span>
            </header>
            <div className="empty">
              Per-SKU revenue and recent invoice lines will appear here once the
              detail-fetch pass syncs <code>items_data</code> into{' '}
              <code>invoice_lines</code>.
            </div>
          </section>
        </>
      )}
    </>
  );
}

/**
 * Back link that returns to the exact list URL (page, search, filters,
 * sort) the user came from. The SkusListPage passes its current URL as
 * `state.from` when navigating to a detail; if absent (deep link),
 * fall back to a plain /skus.
 */
function BackToSkus() {
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  return (
    <Link to={from ?? '/skus'} className="back-link">
      ← All SKUs
    </Link>
  );
}
